"use client";

import { c as _c } from "react/compiler-runtime";
import { useCallback, useState } from "react";
import { HiXMark } from "react-icons/hi2";
import { LuClock } from "react-icons/lu";
import { ProgressBarLink } from "@/components/progress-bar";
import { useTimezoneInfo } from "@/hooks/use-timezone";
import { cx } from "@/lib/tailwind-utils";
import { useUpdateTimezoneOverrideMutation } from "@/query/settings/preferences";
const DISMISSED_KEY = "timezone-banner-dismissed";

/** Short timezone abbreviation (e.g. "EST", "PST"), falling back to the IANA name. */
function timezoneAbbreviation(timezone: string): string {
  return new Intl.DateTimeFormat("en-US", {
    timeZone: timezone,
    timeZoneName: "short"
  }).formatToParts(new Date()).find(part => part.type === "timeZoneName")?.value ?? timezone;
}

/**
 * Dismissible banner shown when the user has a timezone override
 * that differs from their browser's timezone.
 * Dismissal persists for the browser session via sessionStorage.
 */
export function TimezoneBanner(t0) {
  const $ = _c(24);
  const {
    className
  } = t0;
  const {
    timezone,
    isOverridden,
    browserTimezone
  } = useTimezoneInfo();
  const updateTimezoneOverride = useUpdateTimezoneOverrideMutation();
  const [dismissed, setDismissed] = useState(_temp);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      setDismissed(true);
      try {
        sessionStorage.setItem(DISMISSED_KEY, "true");
      } catch {}
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const handleDismiss = t1;
  if (!isOverridden || dismissed) {
    return null;
  }
  let t2;
  if ($[1] !== timezone) {
    t2 = timezoneAbbreviation(timezone);
    $[1] = timezone;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const shortName = t2;
  let t3;
  if ($[3] !== browserTimezone) {
    t3 = timezoneAbbreviation(browserTimezone);
    $[3] = browserTimezone;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const browserShortName = t3;
  const sharedShortName = shortName === browserShortName;
  const abbreviation = sharedShortName ? timezone : shortName;
  const browserAbbreviation = sharedShortName ? browserTimezone : browserShortName;
  let t4;
  if ($[5] !== className) {
    t4 = cx("flex items-center justify-between gap-2 bg-status-info-bg px-4 py-2 text-body text-status-info-text", className);
    $[5] = className;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <LuClock className="h-4 w-4 shrink-0" />;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== abbreviation) {
    t6 = <ProgressBarLink href="/settings/preferences" className="font-medium underline underline-offset-2">{abbreviation}</ProgressBarLink>;
    $[8] = abbreviation;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== browserTimezone || $[11] !== updateTimezoneOverride) {
    t7 = () => updateTimezoneOverride.mutate(browserTimezone);
    $[10] = browserTimezone;
    $[11] = updateTimezoneOverride;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] !== browserAbbreviation || $[14] !== t7 || $[15] !== updateTimezoneOverride.isPending) {
    t8 = <button type="button" onClick={t7} disabled={updateTimezoneOverride.isPending} className="font-medium underline underline-offset-2 disabled:opacity-50">Switch to {browserAbbreviation}</button>;
    $[13] = browserAbbreviation;
    $[14] = t7;
    $[15] = updateTimezoneOverride.isPending;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] !== t6 || $[18] !== t8) {
    t9 = <div className="flex items-center gap-2">{t5}<span>Times are shown in{" "}{t6}, which differs from your browser's timezone.{" "}{t8}</span></div>;
    $[17] = t6;
    $[18] = t8;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  let t10;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <button onClick={handleDismiss} className="shrink-0 rounded p-0.5 hover:bg-status-info-text/10" aria-label="Dismiss timezone banner"><HiXMark className="h-4 w-4" /></button>;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  let t11;
  if ($[21] !== t4 || $[22] !== t9) {
    t11 = <div className={t4}>{t9}{t10}</div>;
    $[21] = t4;
    $[22] = t9;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  return t11;
}
function _temp() {
  try {
    return sessionStorage.getItem(DISMISSED_KEY) === "true";
  } catch {
    return false;
  }
}